import { BookOpen, FileText, Search } from "lucide-react";

import { CollapsibleSection } from "@/views/collapsible-section";

// "Jak działa Chariot?" — the 3-step workflow explainer. One copy source, two
// variants: the full marketing panel on the logged-out landing page, and a
// compact strip on the signed-in empty homepage. The strip is onboarding:
// Home renders it only before the doctor's first search and drops it once
// they have history (decision 2026-11-03). While shown it is COLLAPSIBLE but
// not dismissable — like every homepage section it folds away via the shared
// CollapsibleSection and persists per browser.
const STEPS = [
  {
    icon: Search,
    title: "Wyszukaj lek",
    text: "Wpisz nazwę substancji czynnej, polską nazwę leku lub kod ATC.",
  },
  {
    icon: FileText,
    title: "Otwórz raport",
    text: "Na górze zobaczysz podsumowanie danych osobno dla płodności, każdego trymestru i karmienia piersią. Niżej są szczegóły z cytatami ze źródeł.",
  },
  {
    icon: BookOpen,
    title: "Sprawdź źródła",
    text: "Każda informacja prowadzi do publikacji albo ChPL, z której pochodzi.",
  },
] as const;

export function HowItWorks({ variant = "full" }: { variant?: "full" | "compact" }) {
  if (variant === "compact") {
    // One informational panel (not three card-shaped items): the recents /
    // starter tiles around this strip are buttons, so keeping the steps inside
    // a single divided surface stops them from reading as clickable too.
    return (
      <CollapsibleSection
        storageKey="how-it-works"
        title="Jak działa Chariot?"
        className="mx-auto w-full max-w-2xl"
      >
        <ol className="mt-4 grid divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-sm sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            {STEPS.map(({ icon: StepIcon, title, text }, index) => (
              <li
                key={title}
                className="flex items-start gap-3.5 px-4 py-4 sm:flex-col sm:gap-3 sm:px-5 sm:py-5"
              >
                <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/[0.09] text-primary">
                  <StepIcon className="h-[18px] w-[18px]" aria-hidden="true" />
                  {/* Visual step number only — the <ol> already numbers steps
                      for assistive tech. */}
                  <span
                    aria-hidden="true"
                    className="absolute -right-1 -top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary text-[10px] font-semibold leading-none text-primary-foreground ring-2 ring-card"
                  >
                    {index + 1}
                  </span>
                </span>
                <div className="min-w-0">
                  {/* font-sans: at 14px the global serif h3 reads as a stray
                      typeface next to the sans labels around it. Body matches
                      the 14px used by every other supporting text on the page
                      (weight and color carry the hierarchy). */}
                  <h3 className="font-sans text-sm font-semibold text-foreground">
                    {title}
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {text}
                  </p>
                </div>
              </li>
            ))}
          </ol>
      </CollapsibleSection>
    );
  }

  return (
    <section
      aria-labelledby="how-chariot-works"
      className="overflow-hidden rounded-2xl border border-border/90 bg-card"
    >
      <div className="grid md:grid-cols-[0.8fr_1.2fr]">
        <div className="flex flex-col justify-center bg-primary px-6 py-10 text-primary-foreground sm:px-10 sm:py-12">
          <h2
            id="how-chariot-works"
            className="text-3xl font-semibold tracking-tight sm:text-4xl"
          >
            Jak działa Chariot?
          </h2>
          <p className="mt-4 max-w-sm text-sm leading-6 text-primary-foreground/75 sm:text-base sm:leading-7">
            Od wyszukania leku po sprawdzenie źródeł, bez przeskakiwania między
            bazami
          </p>
        </div>

        <ol className="divide-y divide-border">
          {STEPS.map(({ icon: StepIcon, title, text }) => (
            <li
              key={title}
              className="flex gap-4 px-6 py-7 sm:gap-5 sm:px-8 sm:py-8"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-background text-primary">
                <StepIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {title}
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-muted-foreground sm:text-base">
                  {text}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
